import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { LunariaApiError, type LunariaClient } from "../api/client.js";
import { WritingView } from "../views/writing/WritingView.js";
import { scriptToProse, patchProseText, passageAtCursor } from "./prose.js";

export interface CowritingHandle {
  prepareSource(): Promise<string>;
  preparePreview(): Promise<{ source: string; target?: NonNullable<ReturnType<typeof passageAtCursor>> }>;
}
interface Props {
  client: LunariaClient; projectId: string; episodeId: string; source: string;
  onSourceChange(source: string): void; aiEnabled: boolean; locale: string;
  onPreviewHere?: () => void; onSignIn?: () => void;
}
interface Draft { text: string; source: string; syncedText: string; }

export const CowritingPane = forwardRef<CowritingHandle, Props>(function CowritingPane(props, ref) {
  const { client, projectId, episodeId, source, onSourceChange, aiEnabled } = props;
  const storageKey = `lunaria-writing:${projectId}:${episodeId}`;
  const [draft, setDraft] = useState<Draft>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) ?? "null") as Draft | null;
      if (saved && typeof saved.text === "string" && typeof saved.source === "string" && typeof saved.syncedText === "string" && saved.text !== saved.syncedText) return saved;
    } catch { /* No local draft. */ }
    const text = scriptToProse(source);
    return { text, source, syncedText: text };
  });
  const current = useRef(draft);
  current.current = draft;
  const live = useRef(true);
  const conversion = useRef<Promise<string> | null>(null);
  const conversionAbort = useRef<AbortController | null>(null);
  const suggestionAbort = useRef<AbortController | null>(null);
  const [suggestion, setSuggestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [suggestionError, setSuggestionError] = useState("");
  const [errorNeedsSignIn, setErrorNeedsSignIn] = useState(false);
  const [suggestionNeedsSignIn, setSuggestionNeedsSignIn] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [autoSuggest, setAutoSuggest] = useState(true);
  const [cursor, setCursor] = useState({ from: 0, to: 0, text: "" });
  const cursorRef = useRef(cursor);
  const [editVersion, setEditVersion] = useState(0);
  const [composing, setComposing] = useState(false);
  const emittedSource = useRef(source);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      conversionAbort.current?.abort();
      suggestionAbort.current?.abort();
    };
  }, []);
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(draft)); }
    catch { setError("Local draft could not be saved. Please copy your text before leaving."); }
  }, [draft, storageKey]);
  useEffect(() => {
    if (source === emittedSource.current) return;
    emittedSource.current = source;
    if (current.current.text !== current.current.syncedText) {
      setError("The script changed elsewhere. Your draft is retained; finish syncing before editing other views.");
      setDraft((old) => ({ ...old, source }));
      return;
    }
    const text = scriptToProse(source);
    setDraft({ text, source, syncedText: text });
  }, [source]);

  const prepareSource = useCallback(async (): Promise<string> => {
    if (conversion.current) {
      await conversion.current;
      if (current.current.text === current.current.syncedText) return current.current.source;
    }
    const snapshot = current.current;
    if (snapshot.text === snapshot.syncedText) return snapshot.source;
    const patched = patchProseText(snapshot.source, snapshot.syncedText, snapshot.text);
    if (!patched && !aiEnabled) {
      const message = "Sign in to sync new prose with AI. Your draft is saved on this device.";
      setError(message);
      throw new Error(message);
    }
    const controller = new AbortController();
    conversionAbort.current = controller;
    setBusy(true);
    setError("");
    const work = (async () => {
      const result = patched ?? (await client.writingAssist(projectId, {
        mode: "convert", text: snapshot.text, source: snapshot.source, episodeId,
      }, controller.signal)).source;
      if (!result) throw new Error("The story could not be synced.");
      if (!live.current) throw new Error("Writing view closed");
      if (current.current.source !== snapshot.source) throw new Error("The script changed. Please retry.");
      await client.writeScene(projectId, episodeId, result);
      if (!live.current) throw new Error("Writing view closed");
      emittedSource.current = result;
      const next = { ...current.current, source: result, syncedText: snapshot.text };
      current.current = next;
      setDraft(next);
      onSourceChange(result);
      return result;
    })();
    conversion.current = work;
    try { return await work; }
    catch (err) {
      if (live.current && !controller.signal.aborted) {
        setError(err instanceof Error ? err.message : String(err));
        setErrorNeedsSignIn(err instanceof LunariaApiError && err.status === 401);
      }
      throw err;
    } finally {
      conversion.current = null;
      if (live.current) setBusy(false);
    }
  }, [aiEnabled, client, episodeId, onSourceChange, projectId]);
  useImperativeHandle(ref, () => ({
    prepareSource,
    preparePreview: async () => {
      const prepared = await prepareSource();
      const target = passageAtCursor(prepared, current.current.text, cursorRef.current.to);
      if (!target && current.current.text.trim()) throw new Error("This passage is not ready to play yet. Place the cursor in a story paragraph and try again.");
      return { source: prepared, ...(target ? { target } : {}) };
    },
  }), [prepareSource]);

  useEffect(() => {
    if (draft.text === draft.syncedText || busy || error || composing) return;
    const timer = setTimeout(() => { void prepareSource().catch(() => {}); }, 2200);
    return () => clearTimeout(timer);
  }, [draft.text, draft.syncedText, busy, error, composing, prepareSource]);

  const requestSuggestion = useCallback(async (selection = cursorRef.current, mode: "continue" | "interaction" = "continue") => {
    if (!aiEnabled || !current.current.text.trim()) return;
    suggestionAbort.current?.abort();
    const controller = new AbortController();
    suggestionAbort.current = controller;
    const text = current.current.text;
    setSuggesting(true);
    setSuggestionError("");
    try {
      const result = await client.writingAssist(projectId, {
        mode, text: text.slice(0, selection.to), suffix: text.slice(selection.to), source: current.current.source, episodeId,
        instruction: selection.text ? `Suggest a short continuation or interaction after this selected passage: ${selection.text}` : undefined,
      }, controller.signal);
      if (!controller.signal.aborted && live.current && current.current.text === text) setSuggestion(result.text);
    } catch (err) {
      if (!controller.signal.aborted && live.current) {
        setSuggestionError(err instanceof Error ? err.message : String(err));
        setSuggestionNeedsSignIn(err instanceof LunariaApiError && err.status === 401);
      }
    } finally {
      if (live.current && suggestionAbort.current === controller) setSuggesting(false);
    }
  }, [aiEnabled, client, episodeId, projectId]);

  useEffect(() => {
    if (composing || !autoSuggest || !editVersion || !aiEnabled || cursor.from !== cursor.to || !draft.text.trim()) return;
    const timer = setTimeout(() => { void requestSuggestion(); }, 800);
    return () => { clearTimeout(timer); suggestionAbort.current?.abort(); };
  }, [editVersion, autoSuggest, aiEnabled, cursor, draft.text, requestSuggestion, composing]);

  return <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
    <WritingView
      value={draft.text}
      onChange={(text) => {
        suggestionAbort.current?.abort(); setSuggesting(false); setSuggestion(""); setError(""); setSuggestionError("");
        const next = { ...current.current, text }; current.current = next; setDraft(next);
        setEditVersion((n) => n + 1);
      }}
      suggestion={suggestion}
      suggesting={suggesting}
      aiEnabled={aiEnabled && autoSuggest}
      onPreviewHere={props.onPreviewHere}
      onDismissSuggestion={() => { suggestionAbort.current?.abort(); setSuggesting(false); setSuggestion(""); }}
      onAcceptSuggestion={() => setSuggestion("")}
      onSelectionChange={(selection) => {
        if (selection.from === cursorRef.current.from && selection.to === cursorRef.current.to) return;
        cursorRef.current = selection;
        suggestionAbort.current?.abort(); setSuggesting(false); setSuggestion(""); setCursor(selection);
      }}
      onCompositionChange={(active) => {
        setComposing(active);
        if (active) { suggestionAbort.current?.abort(); setSuggesting(false); setSuggestion(""); }
      }}
      onRequestSuggestion={aiEnabled ? (selection) => { void requestSuggestion(selection); } : undefined}
      title="Your story"
      status={busy ? "Syncing your story…" : draft.text !== draft.syncedText ? "Draft saved locally" : "Story synced"}
    />
    <div className="cowriting-actions" style={{ display: "flex", gap: 12, padding: "8px 20px", alignItems: "center", fontSize: 12, color: "var(--text-secondary)", flexWrap: "wrap" }}>
      <label><input type="checkbox" checked={autoSuggest && aiEnabled} disabled={!aiEnabled} onChange={(e) => { setAutoSuggest(e.target.checked); setSuggestion(""); setSuggesting(false); suggestionAbort.current?.abort(); }} /> {aiEnabled ? "Automatic suggestions" : "Sign in for AI suggestions"}</label>
      <button type="button" disabled={!aiEnabled} onClick={() => { void requestSuggestion(cursorRef.current, "interaction"); }}>Suggest a choice</button>
      {error && <span role="status">{error} <button type="button" onClick={() => { setError(""); void prepareSource().catch(() => {}); }}>Retry sync</button></span>}
      {suggestionError && <span role="status">Suggestions unavailable: {suggestionError} <button type="button" onClick={() => { void requestSuggestion(); }}>Retry suggestions</button></span>}
      {props.onSignIn && (!aiEnabled || (error && errorNeedsSignIn) || (suggestionError && suggestionNeedsSignIn)) && <button type="button" onClick={props.onSignIn}>{aiEnabled ? "Sign in again" : "Sign in"}</button>}
    </div>
  </div>;
});
